import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProfileProvider } from "./context/ProfileContext";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import ComingSoon from "./pages/ComingSoon";
import ProfilesPage from "./pages/ProfilesPage";
import QuestMapPage from "./pages/QuestMapPage";
import CaveMapPage from "./pages/CaveMapPage";
import CavePage from "./pages/CavePage";
import GamePage from "./pages/GamePage";
import ParentPortal from "./pages/ParentPortal";
import AdminPage from "./pages/AdminPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import InstallPrompt from "./components/InstallPrompt";

function Loading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "linear-gradient(180deg,#38BDF8,#86EFAC)" }}>
      <div className="text-7xl animate-floaty">📚</div>
      <p className="font-display text-2xl font-black text-white mt-4 animate-pulse">Loading...</p>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) return <Navigate to="/profiles" replace />;
  return children;
}

function SiteGate({ children }) {
  const [granted, setGranted] = React.useState(() => localStorage.getItem("pq_site_access") === "granted");
  if (!granted) return <ComingSoon onUnlock={() => setGranted(true)} />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Returning from Google OAuth: process the session before any route/auth check runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><AuthPage /></PublicOnly>} />
      <Route path="/profiles" element={<Protected><ProfilesPage /></Protected>} />
      <Route path="/map" element={<Protected><QuestMapPage /></Protected>} />
      <Route path="/chapter/:chapterId" element={<Protected><CaveMapPage /></Protected>} />
      <Route path="/chapter/:chapterId/cave/:caveId" element={<Protected><CavePage /></Protected>} />
      <Route path="/game/:levelId" element={<Protected><GamePage /></Protected>} />
      <Route path="/parent" element={<Protected><ParentPortal /></Protected>} />
      <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="*" element={<Navigate to="/profiles" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SiteGate>
        <AuthProvider>
          <ProfileProvider>
            <AppRoutes />
            <InstallPrompt />
            <Toaster position="top-center" richColors />
          </ProfileProvider>
        </AuthProvider>
      </SiteGate>
    </BrowserRouter>
  );
}
